import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { theme } from '../../material-ui/theme';
import { Box } from '@mui/system';
import { Grid, Typography } from '@mui/material';
import { getTimeFormat } from '../../utils';
import { tenantSelector } from '../../selector/authenticationSelector';
import { palette } from '../../material-ui/palette';
import { RootState } from '../../store/store';

const PREFIX = 'BookingTicket';

const classes = {
    rightTextAlign: `${PREFIX}-rightTextAlign`,
    headerLogo: `${PREFIX}-headerLogo`,
    ticket_dot_left: `${PREFIX}-ticket_dot_left`,
    ticket_dot_right: `${PREFIX}-ticket_dot_right`,
    ticket_class: `${PREFIX}-ticket_class`,
    logo: `${PREFIX}-logo`,
};

const StyledBox = styled(Box)(() => ({
    color: palette.text.secondary || 'white',
    borderRadius: 10,
    position: 'relative',
    margin: `0 ${theme.spacing(0.25)} 15px ${theme.spacing(0.25)}`,
    overflow: 'hidden',
    // /* Scale ticket down for iPhone 5; not optimal but usable */
    '@media only screen and (max-height: 555px)': {
        transform: 'scale(0.75)',
        transformOrigin: 'top',
    },
    [theme.breakpoints.up('md')]: {
        width: 350,
        // height: 455,
    },
    [`& .${classes.rightTextAlign}`]: {
        color: palette.text.secondary || 'white',
        textAlign: 'right',
    },
    [`& .${classes.headerLogo}`]: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '50%',
        height: '44px',
        marginTop: '35px',
        paddingLeft: '15px',
        paddingRight: '3px',
        [theme.breakpoints.down('md')]: {
            height: '33px',
        },
    },
    [`& .${classes.ticket_dot_left}`]: {
        width: '5px',
        height: '10px',
        position: 'absolute',
        left: 0,
        borderTopRightRadius: '5px',
        borderBottomRightRadius: '5px',
        top: '29%',
    },
    [`& .${classes.ticket_dot_right}`]: {
        width: '5px',
        height: '10px',
        position: 'absolute',
        right: 0,
        borderTopLeftRadius: '5px',
        borderBottomLeftRadius: '5px',
        top: '29%',
    },
    [`& .${classes.ticket_class}`]: {
        width: '100%',
        padding: '10px 15px',
        margin: '4px 0px',
    },
    [`& .${classes.logo}`]: {
        height: '50px',
        marginTop: '5px',
        [theme.breakpoints.down('md')]: {
            height: '24px',
        },
    },
}));

interface BookingTicketProps {
    order?: any;
    location?: string;
    reservationStartDate: any;
    reservationEndDate: any;
    amount?: number;
    size?: string; // Hardcoded for now, need to implement an API to retrieve it
}

const BookingTicket = (props: BookingTicketProps) => {
    const { order, location, reservationStartDate, reservationEndDate, size } =
        props;
    const { t } = useTranslation();

    const selectedTenant = useSelector(tenantSelector);

    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    function findTenantLogo() {
        return config?.logo;
    }

    return (
        <StyledBox className={' text-foreground/10'}>
            <Box className={classes.ticket_dot_left + ' bg-foreground'} />
            <Grid container={true} spacing={2} direction={'column'}>
                <Grid
                    item={true}
                    container={true}
                    justifyContent={'space-between'}
                    direction={'row'}
                >
                    <Box className={classes.headerLogo + ' text-background'}>
                        <img
                            src={findTenantLogo()}
                            className={classes.logo}
                            alt={selectedTenant!}
                        />
                    </Box>
                </Grid>
            </Grid>
            <Grid
                container={true}
                className={classes.ticket_class}
                spacing={2}
                direction={'column'}
            >
                <Grid
                    item={true}
                    container={true}
                    spacing={2}
                    justifyContent={'space-between'}
                    direction={'row'}
                >
                    <Grid item={true} justifyContent={'flex-start'}>
                        <Typography
                            className={'text-background text-base font-normal'}
                        >
                            {t('visitor_booking.ticket.start')}
                        </Typography>
                        <Typography
                            className={'text-background text-base font-normal'}
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            <strong>
                                {reservationStartDate?.format(
                                    t('formats.date'),
                                )}
                            </strong>

                            <strong>
                                {getTimeFormat(reservationStartDate)}
                            </strong>
                        </Typography>
                    </Grid>
                    <Grid item={true} spacing={2} justifyContent={'flex-end'}>
                        <Box style={{ textAlign: 'right' }}>
                            <Typography
                                className={
                                    'text-background text-base font-normal'
                                }
                            >
                                {t('visitor_booking.ticket.end')}
                            </Typography>
                            <Typography
                                className={
                                    'text-background text-base font-normal'
                                }
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <strong>
                                    {reservationEndDate?.format(
                                        t('formats.date'),
                                    )}
                                </strong>
                                <strong>
                                    {getTimeFormat(reservationEndDate)}
                                </strong>
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid item={true}>
                    <Typography
                        className={'text-background text-base font-normal'}
                    >
                        {t('visitor_booking.ticket.location')}
                    </Typography>
                    <Typography
                        className={'text-background text-base font-normal'}
                    >
                        <strong>{location}</strong>
                    </Typography>
                </Grid>
                <Grid item={true}>
                    <Typography
                        className={'text-background text-base font-normal'}
                    >
                        {t('visitor_booking.ticket.booked_item')}
                    </Typography>
                    <Typography
                        className={'text-background text-base font-normal'}
                    >
                        <strong>
                            {t('visitor_booking.ticket.amount_and_size', {
                                amount: props?.amount,
                                size: size,
                            })}
                        </strong>
                    </Typography>
                </Grid>
                {order ? (
                    <Grid item={true}>
                        <Typography
                            className={'text-background text-base font-normal'}
                        >
                            {t('visitor_booking.ticket.booking_number')}
                        </Typography>
                        <Typography
                            className={'text-background text-base font-normal'}
                        >
                            <strong>{order?.bookingNumber}</strong>
                        </Typography>
                    </Grid>
                ) : null}
            </Grid>
            <Box className={classes.ticket_dot_right + ' bg-foreground'} />
        </StyledBox>
    );
};

export default BookingTicket;
